<template>
  <div v-if="state.userHasAccess" class="flex flex-col w-full full-viewport-height">
    <div v-if="wallLocked" class="absolute top-16 right-24 z-101 flex justify-center items-center">
      <Notice :type="DialogMessageType.Warning" :text="t('lockedwall')" />
    </div>
    <NewNotePopup
      v-if="state.newNoteMenuIsVisible"
      :place-new-note-on-board="!sidebarIsVisible"
      :selected-board-id="filterStore.getters.selectedBoardId.value"
      @note-added="handleNoteAdded"
      @close-popup="toggleNewNoteMenu()"
    />
    <MenuBar v-if="state.wall" :wall="state.wall" :is-attached-to-wall="true" @set-task-id="setTaskId" />
    <ConfirmationPopup
      v-if="state.deleteNoteIsVisible"
      :text="t('deletionwarningnote')"
      @confirm="deleteNote()"
      @close-popup="toggleDeleteNoteConfirmationPopup()"
    />
    <NewBoardPopup v-if="state.newBoardPopupVisible" @close-popup="state.newBoardPopupVisible = false" />

    <div v-if="commonStore.getters.screensizeType.value !== ScreensizeType.phone" id="wall-wrapper" class="flex flex-grow min-h-0 relative">
      <div class="absolute fadein text-slate-white pointer-events-none h-full">
        <div class="flex flex-row relative z-10 pointer-events-none h-full">
          <WallSidebar
            class="w-0 transition-all ease-in pointer-events-auto"
            :wall="state.wall"
            :class="{
              'w-56': commonStore.getters.screensizeType.value === ScreensizeType.tablet && sidebarIsVisible,
              'w-96': commonStore.getters.screensizeType.value === ScreensizeType.desktop && sidebarIsVisible,
              'w-0': !sidebarIsVisible,
            }"
            :notes="state.notes"
            :drag-is-ongoing="state.dragIsOngoing"
            :render="state.sidebarRender"
            :restricted-action-allowed="restrictedActionAllowed"
            :filter-entries="state.filterEntries"
            :selected-board="selectedBoard"
            :locked="wallLocked"
            @update-sidebar-value="updateSidebarValue"
            @initiate-note-drag="startSidebarNoteDrag"
            @toggle-new-note-menu="toggleNewNoteMenu"
            @delete-note="prepareNoteDeletion"
          />
          <SidebarControls
            v-if="state.wall"
            class="pointer-events-none"
            :sidebar-is-visible="state.sidebarIsVisible"
            :sidebar-note-being-dragged="state.noteBeingDragged"
            :restricted-action-allowed="restrictedActionAllowed"
            :wall="state.wall"
            :scale="wall?.scale ?? 1.2"
            :can-zoom-in="wall?.canZoomIn ?? false"
            :can-zoom-out="wall?.canZoomOut ?? false"
            @create-board="() => (state.newBoardPopupVisible = true)"
            @toggle-sidebar="toggleSidebar"
            @toggle-new-note-menu="toggleNewNoteMenu"
            @zoom-to-scale="wall?.zoomToScale"
          />
        </div>
      </div>
      <WallVue
        v-if="state.wall"
        ref="wall"
        class="fadein"
        :class="{
          'border-2': state.highlightDropTarget,
        }"
        :wall="state.wall"
        :notes="state.notes"
        :sidebar-note-being-dragged="state.noteBeingDragged"
        :restricted-action-allowed="restrictedActionAllowed"
        :sidebar-value="state.sidebarValue"
        :zoom-index="state.zoomIndex"
        @toggle-new-note-menu="toggleNewNoteMenu"
      />

      <!-- Draggable sidebar note, not displayed initially-->
      <div
        ref="draggableElement"
        class="draggable size-fit rounded-lg absolute z-50 touch-none"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerCancel"
        :style="{
          top: `${state.noteBeingDragged.y}px`,
          left: `${state.noteBeingDragged.x}px`,
          transform: state.dragIsOngoing ? `translate(${deltaPosition.x}px, ${deltaPosition.y}px)` : '',
        }"
      >
        <SidebarNote
          v-if="state.noteBeingDragged.note"
          :style="{ width: `${state.sidebarValue}px` }"
          :note="state.noteBeingDragged.note"
          :wall-id="filterStore.getters.selectedBoardId.value"
          :locked="false"
        />
      </div>
    </div>
    <div v-else-if="commonStore.getters.screensizeType.value === ScreensizeType.phone" id="wall-wrapper" class="flex w-full h-full min-h-0">
      <WallSidebar
        v-show="sidebarIsVisible"
        class="fadein bg-slate-middle text-slate-white z-50 w-0"
        :class="{
          'w-full': sidebarIsVisible,
        }"
        :notes="state.notes"
        :drag-is-ongoing="state.dragIsOngoing"
        :render="state.sidebarRender"
        :restricted-action-allowed="restrictedActionAllowed"
        :filter-entries="state.filterEntries"
        :selected-board="selectedBoard"
        :locked="wallLocked"
        @update-sidebar-value="updateSidebarValue"
        @toggle-new-note-menu="toggleNewNoteMenu"
        @delete-note="prepareNoteDeletion"
      />
    </div>
  </div>
  <LoadingScreen v-else />
</template>

<script setup lang="ts">
  import { onBeforeMount, reactive, onMounted, computed, watch, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { FilterType, GroupType, ParcelType, PermissionLevel, ScreensizeType } from '@/constants'
  import { Note, Wall, Board, Parcel, DialogMessageType, BoardEntry, User } from '@/common/classes'
  import { FilterEntry, StoreResponse } from '@/common/interfaces'

  import { useNoteStore } from '@/composables/noteStore'
  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useDialogStore from '@/composables/dialog'
  import useCommonStore from '@/composables/common'
  import useFilterStore from '@/composables/filter'
  import useParcelStore from '@/composables/parcel'

  import WallVue from '@/components/wall/WallView.vue'
  import MenuBar from '@/components/common/MenuBar.vue'
  import WallSidebar from '@/components/wall/WallSidebar.vue'
  import NewNotePopup from '@/components/popups/NewNotePopup.vue'
  import LoadingScreen from '@/components/common/LoadingScreen.vue'
  import ConfirmationPopup from '@/components/popups/ConfirmationPopup.vue'
  import SidebarNote from '../../components/wall/SidebarNote.vue'
  import SidebarControls from '../../components/wall/SidebarControls.vue'
  import NewBoardPopup from '@/components/popups/NewBoardPopup.vue'

  import Notice from '@/components/dialog/Notice.vue'
  import { DragData, useDraggable } from '@/composables/useDraggable'

  export interface DraggedNote {
    note?: Note // note ID
    x: number // pixels
    y: number // pixels
    el: HTMLElement | null
  }

  const commonStore = useCommonStore()
  const filterStore = useFilterStore()
  const wallStore = useWallStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const dialogStore = useDialogStore()
  const parcelStore = useParcelStore()
  const router = useRouter()
  const route = useRoute()

  const { isMobileView } = commonStore.getters

  const state = reactive({
    sidebarValue: 0,
    sidebarRender: 0, // used to explicitly re-trigger rendering the DOM for the sidebar

    dragPosition: [0, 0],
    noteId: '',
    dragIsOngoing: false,
    dragStartX: 0,
    dragStartY: 0,

    noteBeingDragged: { note: undefined, x: 0, y: 0, el: null } as DraggedNote,

    zoomIndex: 3,

    wall: {} as Wall | null,
    notes: [] as Note[],
    noteToDelete: {} as Note | null,
    filterEntries: [] as FilterEntry[],

    deleteNoteIsVisible: false,
    newBoardPopupVisible: false,
    newNoteMenuIsVisible: false,
    userHasAccess: false,
    sidebarIsVisible: false,
    highlightDropTarget: false,
  })

  const messages = {
    no: {
      blue: 'Blå',
      white: 'Hvit',
      gray: 'Grå',
      noTasks: 'Ingen oppgaver tilgjengelig på denne vegg',
    },
    en: {
      blue: 'Blue',
      white: 'White',
      gray: 'Gray',
      noTasks: 'No tasks assigned to this wall',
    },
  }

  const { t } = useI18n({ messages })

  const draggableElement = ref<HTMLElement | null>(null)
  const wall = ref<InstanceType<typeof WallVue> | null>(null)

  //AllowBoardCreation should be computed to provide reactivity
  const allowBoardCreationFlags = computed(() => wallStore.getters.selectedWall.value?.tasks.map((task) => task.allowBoardCreation) ?? [])

  onBeforeMount(async () => {
    await loadWall()
  })

  onMounted(() => {
    state.sidebarIsVisible = true
    updateNotesAndFilters()
    window.scrollTo(0, 0)
  })

  const updateZoomIndex = (newValue: number) => (state.zoomIndex = newValue)

  // -------------   2025 New sidebar note dragging functions ----------------------

  let rect: DOMRect
  function isDragWithinElement(event: PointerEvent) {
    const x = event.clientX
    const y = event.clientY
    return rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }

  const { isDragging, pointerId, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel, startPosition, deltaPosition } =
    useDraggable(draggableElement, {
      onDragStart: (event: PointerEvent, data) => {
        if (wall.value) rect = wall.value.$el.getBoundingClientRect()
      },
      onDragMove: (event: PointerEvent, data: DragData) => {
        const withinBounds = isDragWithinElement(event)
        if (state.noteBeingDragged && withinBounds) {
          state.highlightDropTarget = true
        } else {
          state.highlightDropTarget = false
        }
      },
      onDragEnd: async (event: PointerEvent, data: DragData) => {
        state.highlightDropTarget = false
        const withinBounds = isDragWithinElement(event)
        if (state.noteBeingDragged.note && withinBounds) {
          await wall.value?.sidebarNoteDragEnd(event, state.noteBeingDragged.note)
        }
        state.noteBeingDragged = {
          note: undefined,
          x: 0,
          y: 0,
          el: null,
        }
        state.dragIsOngoing = false
      },
    })

  function startSidebarNoteDrag(note: Note, event: PointerEvent) {
    state.dragIsOngoing = true
    const pageY = event.pageY
    const pageX = event.pageX
    state.noteBeingDragged = {
      note,
      x: pageX - state.sidebarValue / 2,
      y: pageY - 100,
      el: draggableElement.value,
    }
    handlePointerDown(event)
  }

  // ---------------------------------------------------------------------------------

  watch(
    [
      () => noteStore.getters.sortedNotes.value, // update sidebar with a copied noted if another client makes a copy of a note
      filterStore.getters.selectedBoardId,
      filterStore.getters.selectedFilterId,
      filterStore.getters.selectedTaskId,
    ],
    () => {
      updateNotesAndFilters()
      hydrateFilter()
      state.filterEntries = filterStore.actions.sortFilterEntries()
    },
  )

  watch(
    () => wallStore.getters.selectedWall.value,
    (newValue) => {
      if (newValue) {
        state.wall = new Wall(newValue)
        // we need to keep the local state in order to modify a copy of the selected wall without altering it's state
        //  in the store...
      }
    },
    { deep: true },
  )

  //Watch for changes in allowBoardCreation to update components accordingly.
  watch(
    allowBoardCreationFlags,
    () => {
      updateNotesAndFilters()
      hydrateFilter()
    },
    { deep: true },
  )

  watch(isMobileView, () => {
    router.go(0) // force page reload on desktop > tablet breakpoint reach
  })

  const wallLocked = computed(() => {
    const isReadOnly = state.wall?.status.readOnly
    const userIsOwner = userStore.getters.user.value?._id === state.wall?.users.owner.user_id
    return isReadOnly && !userIsOwner
  })

  const restrictedActionAllowed = computed(() => {
    const isReadOnly = state.wall?.status.readOnly
    const userIsOwner = userStore.getters.user.value?._id === state.wall?.users.owner.user_id
    // determine if the user is allowed to perform restricted actions
    if (state.wall && userStore.getters.user.value) return !isReadOnly || userIsOwner
    else return false
  })

  const sidebarIsVisible = computed(() => {
    switch (commonStore.getters.screensizeType.value) {
      case ScreensizeType.desktop:
        return state.sidebarIsVisible
      case ScreensizeType.tablet:
        return state.sidebarIsVisible
      default:
        return true // default to true on phone
    }
  })

  const selectedBoard = computed(() => {
    return wallStore.getters.selectedBoard.value ? wallStore.getters.selectedBoard.value : ({} as Board)
  })

  const sidebarStyles = computed(() => {
    switch (commonStore.getters.screensizeType.value) {
      case ScreensizeType.desktop:
        return 'w-1/4'
      case ScreensizeType.tablet:
        return !state.sidebarIsVisible ? '' : 'w-1/3'
      default:
        return 'w-full'
    }
  })

  //Handle notes if added afer board toggle on/off
  function handleNoteAdded() {
    updateNotesAndFilters()
    hydrateFilter()
  }

  function updateNotesAndFilters() {
    const taskBasedNotes = noteStore.getters.sortedNotes.value.filter((n: Note) => n.task_id === filterStore.getters.selectedTaskId.value)
    state.notes = taskBasedNotes
  }

  /*   function wallStyles() {
    switch (commonStore.getters.screensizeType.value) {
      case ScreensizeType.desktop:
        return !state.sidebarIsVisible ? 'w-full' : 'w-3/4'
      case ScreensizeType.tablet:
        return !state.sidebarIsVisible ? 'w-full' : 'w-2/3'
      default:
        return 'w-0'
    }
  } */

  function canvasCourseIdFromHandoff(): string {
    try {
      const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ltiHandoff') : null
      if (!token) return ''
      const payloadPart = token.split('.')[1]
      if (!payloadPart) return ''
      const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))) as { course?: string }
      return payload.course || ''
    } catch {
      return ''
    }
  }

  function userHasFeideGroupShare(wall: Wall, user: User): boolean {
    const groupIds = (user.profile.groups ?? []).map((g) => g.originalId?.toString()).filter(Boolean)
    return wall.users.shares.some(
      (share) => share.groupType === GroupType.DATAPORTEN && groupIds.includes(String(share.group_id)),
    )
  }

  function joinBoardName(user: User): string {
    if (!user.status.isRegistered) return (route.query.bId as string) || user.profile.username
    return user.profile.username
  }

  function shouldCreateJoinBoard(user: User, wall: Wall): boolean {
    if (!user.status.isRegistered) return true
    if (commonStore.actions.hasMinimumPermission(user, PermissionLevel.Organize, wall)) return false
    if (user.profile.provider === 'CANVAS') {
      const courseId = canvasCourseIdFromHandoff()
      if (!courseId) return false
      if (wall.boards.some((board) => board.name === courseId)) return false
      return true
    }
    if (user.profile.provider === 'DATAPORTEN') return userHasFeideGroupShare(wall, user)
    return false
  }

  async function connectToMQTTAndSetupWall() {
    try {
      const user = userStore.getters.user.value
      if (user) {
        await parcelStore.actions.openMQTTConnection(user)
        if (state.wall) {
          const id = state.wall._id
          await parcelStore.actions.unSubscribeTopic(user._id) // unsubscribe and re-subscribe (to prevent granted from being empty)
          await parcelStore.actions.subscribeTopic(user._id)
          await parcelStore.actions.subscribeTopic(id)
          for (const groupId of user.profile.groups.map((g) => g.originalId)) {
            await parcelStore.actions.unSubscribeTopic(groupId)
            await parcelStore.actions.subscribeTopic(groupId)
          }

          const newBoardName = joinBoardName(user)
          if (newBoardName && state.wall.boards.findIndex((b: Board) => b.name === newBoardName) === -1 && shouldCreateJoinBoard(user, state.wall)) {
            state.wall.addBoard({ name: newBoardName, createdAt: new Date() } as Board)
            const res = await wallStore.actions.updateWall(state.wall)
            const persisted = wallStore.getters.selectedWall.value
            if (res.success && persisted?.boards.some((board) => board.name === newBoardName)) {
              state.wall = new Wall(persisted)
              const parcel = new Parcel({ parcelType: ParcelType.BoardAdded, body: state.wall }, user, state.wall)
              await parcelStore.actions.sendParcel(id, parcel)
            }
          }
          setTimeout(async () => {
            state.userHasAccess = true
            if (state.wall) {
              const parcel = new Parcel({ parcelType: ParcelType.UserConnect }, user, state.wall)
              await parcelStore.actions.sendParcel(id, parcel)
            }
          }, 600) // displays the "wall" loading screen for 0.6 seconds
        }
      }
    } catch (e) {
      const cb = () => {
        router.push('/dashboard')
      }
      dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('mqtterror'), 5000, cb)
    }
  }

  async function loadWall() {
    state.userHasAccess = false
    const handoff = route.query.handoff
    if (typeof handoff === 'string' && handoff.length > 0) {
      sessionStorage.setItem('ltiHandoff', handoff)
      const query = { ...route.query }
      delete query.handoff
      await router.replace({ path: '/wall', query })
    }
    const id = route.query.id?.toString()
    if (id) {
      try {
        await userStore.actions.getUserDetails()
        const res = await wallStore.actions.getWall(id)
        const wall = wallStore.getters.selectedWall.value // new wall value only available after retrieving it...
        if (res.success && wall) {
          // valid session and wall access granted
          state.wall = new Wall(wall) // update value with full wall details
          const defaultTaskId = state.wall.tasks[0]?._id
          if (!defaultTaskId) {
            console.warn(t('noTasks') + ' ' + wall.details.title)
            dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('noTasks') + ' ' + wall.details.title, 5000)
            router.push('/401')
          }
          const noteRes: StoreResponse = await noteStore.actions.getNotes(state.wall._id)
          if (noteRes.success) {
            await connectToMQTTAndSetupWall()
            setTaskId(defaultTaskId) // first task as default
            hydrateFilter() // set filter store entries based on wall and it's notes
          } else router.push('/dashboard')
        } else router.push('/401') // no permission to access wall
      } catch (e) {
        console.error(e)
        router.push('/401') // default response on error
      }
    } else {
      router.push('/404') // 404 if no wall id was given (malformed URL)
    }
  }

  /* hydrateFilter()
    this function is hydrating the filter in the sidebar according to the selected filter option
    note that the result is assembled based on the filtertype. The way filter entries are added
    differs based on what the filter is doing...
  */
  function hydrateFilter() {
    const filterId = filterStore.state.selectedFilterId

    filterStore.actions.resetAllFilters()
    const expression = commonStore.getters.screensizeType.value === ScreensizeType.phone
    // show all notes on phone, not just the ones on the selected board
    const base = expression ? state.notes : state.notes.filter((note) => note.entries[filterStore.state.selectedBoardId])
    if (filterId === FilterType.HIGHLIGHTS) {
      for (const note of base) {
        const entries = expression ? Object.values(note.entries) : [note.entries[filterStore.state.selectedBoardId]]
        // display only the notes of the current board for this filter
        for (const entry of entries as BoardEntry[]) {
          if (entry !== undefined) {
            let colorName = ''
            switch (entry.highlightColor) {
              case 'bg-blue-middle text-slate-white':
                colorName = t('blue')
                break
              case 'bg-white-bright text-slate-dark':
                colorName = t('white')
                break
              default:
                colorName = t('gray')
                break
            }
            filterStore.actions.addFilterEntry(colorName, [note._id], filterId)
          }
        }
      }
    } else {
      for (const note of state.notes) {
        if (filterId === FilterType.TIME) {
          // date-based filter
          filterStore.actions.addFilterEntry(new Date(note.status.created).toLocaleDateString(), [note._id], filterId)
        } else if (filterId === FilterType.BOARDS && state.wall) {
          // board-name based filter
          for (let id in note.entries) {
            const board = state.wall.boards.find((b: Board) => b._id === id)
            if (board !== undefined) {
              filterStore.actions.addFilterEntry(board.name, [note._id], filterId)
            }
          }
        } else if (filterId === FilterType.USERS) {
          // user-name based filter
          filterStore.actions.addFilterEntry(note.status.createdBy.username, [note._id], filterId)
        } else if (filterId === FilterType.TAGS) {
          // tag (#) based filter
          for (let tag of note.tags) {
            filterStore.actions.addFilterEntry(tag, [note._id], filterId)
          }
        } else if (filterId === FilterType.NOTAGS && note.tags.length === 0) {
          // notes with no tags only
          filterStore.actions.addFilterEntry(t('notags'), [note._id], filterId)
        }
      }
    }
  }

  function toggleDeleteNoteConfirmationPopup() {
    state.deleteNoteIsVisible = !state.deleteNoteIsVisible
  }

  function prepareNoteDeletion(note: Note) {
    state.noteToDelete = note
    toggleDeleteNoteConfirmationPopup()
  }

  async function deleteNote() {
    const user = userStore.getters.user.value
    if (state.wall && user && state.noteToDelete) {
      const hasPermission =
        state.noteToDelete.status.createdBy.user_id === user._id ||
        commonStore.actions.hasMinimumPermission(user, PermissionLevel.Organize, state.wall)
      if (hasPermission) {
        let note = new Note(state.noteToDelete) // create local copy without altering the current note just yet...
        const res = await noteStore.actions.updateNote(note, state.wall._id, ParcelType.MessageDelete)
        if (res.success && state.wall) {
          const parcel = new Parcel({ parcelType: ParcelType.MessageDelete, body: note }, user, state.wall)
          await parcelStore.actions.sendParcel(state.wall._id, parcel)
          if (state.noteToDelete) noteStore.actions.localDelete(state.noteToDelete)
          state.noteToDelete = null
        }
      }
    }
  }

  async function setTaskId(id: string) {
    filterStore.actions.resetAllFilters()
    filterStore.actions.resetFilteredNotes()
    filterStore.actions.setTaskId(id) // other components will retrieve the id on different actions
    const user = userStore.getters.user.value
  }

  function updateSidebarValue(value: number) {
    state.sidebarValue = value
  }

  function toggleSidebar() {
    state.sidebarIsVisible = !state.sidebarIsVisible
    //state.sidebarRender++
  }

  function toggleNewNoteMenu() {
    state.newNoteMenuIsVisible = !state.newNoteMenuIsVisible
  }
</script>

<style scoped>
  .draggable {
    touch-action: auto;
    cursor: grab;
    background: #f0f0f0;
    user-select: none;
    transition: transform 0.1s ease-out;
  }

  .draggable:active {
    cursor: grabbing;
  }
</style>
